import React from 'react';

type ConfigObject = Record<string, unknown>;

interface StrategyConfigPreviewProps {
  config: ConfigObject;
}

const isConfigObject = (value: unknown): value is ConfigObject =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const StrategyConfigPreview: React.FC<StrategyConfigPreviewProps> = ({ config }) => {
  const symbols = Array.isArray(config.symbols) ? config.symbols : [];
  const hasSymbols = Boolean(config.symbols);
  const symbolConfig = isConfigObject(config.symbol_config) ? config.symbol_config : {};

  return (
    <div className="ux-scrollbar h-full overflow-y-auto pr-3 space-y-5">
      <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
          <h4 className="text-sm text-blue-400 uppercase font-black tracking-[0.2em]">Global Control Deck</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
          {Object.entries(config).map(([key, value]) => {
            if (key === 'symbol_config' || key === 'symbols' || typeof value === 'object') return null;
            const isLeverage = key === 'leverage' || key === 'position_size';
            return (
              <div key={key} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0 group overflow-hidden">
                <span className={`text-sm font-bold tracking-tight break-all mr-4 ${isLeverage ? 'text-blue-300' : 'text-white/40 group-hover:text-white/60 transition-colors'}`}>{key}</span>
                <div className="flex-shrink-0 flex items-center gap-2">
                  <span className={`text-sm font-mono font-bold ${isLeverage ? 'text-neon px-2 py-0.5 bg-neon/10 rounded-sm border border-neon/20' : 'text-white/80'}`}>
                    {typeof value === 'boolean' ? (value ? 'ACTIVE' : 'INERT') : String(value)}
                    {isLeverage ? 'x' : ''}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {hasSymbols && (
        <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-neon shadow-[0_0_8px_var(--neon)]" />
            <h4 className="text-sm text-neon uppercase font-black tracking-[0.2em]">Authorized Tickertape</h4>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {symbols.map((symbol, index) => (
              <div key={`${String(symbol)}-${index}`} className="px-4 py-1.5 bg-neon/5 text-neon text-xs font-black rounded-lg border border-neon/20 tracking-tighter">
                {String(symbol)}
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(symbolConfig).length > 0 && (
        <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <h4 className="text-sm text-amber-400 uppercase font-black tracking-[0.2em]">Symbol Overrides</h4>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {Object.entries(symbolConfig).map(([symbol, value]) => {
              const overrides = isConfigObject(value) ? value : {};
              return (
                <div key={symbol} className="p-4 rounded-xl bg-black/30 border border-white/5 hover:border-white/10 transition-colors">
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="text-sm font-black text-white tracking-widest leading-none">{symbol}</span>
                    <div className="h-px grow bg-white/10" />
                  </div>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
                    {Object.entries(overrides).map(([key, override]) => (
                      <div key={key} className="flex justify-between items-center text-xs overflow-hidden">
                        <span className="text-white/30 font-bold uppercase tracking-widest break-all mr-4">{key}</span>
                        <span className="text-white/90 font-mono font-bold flex-shrink-0">
                          {String(override)}{key === 'leverage' ? 'x' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyConfigPreview;
